import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import StepIndicator from './StepIndicator';
import { UseCaseCard, UseCaseCardSkeleton, UseCaseTile, USE_CASE_META } from './useCaseMeta';
import { MktCard, MktButton, FreeBadge, Pill, Tag, BackLink } from './marketplaceUi';
import {
  getUseCases, getSubCategories, getArchetypes, getMarketplaceState,
  type UseCase, type SubCategory, type Archetype,
} from './marketplaceApi';

/**
 * Free / first study — ported from Free_account_study.html. Two internal
 * screens: (1) choose a use case, (2) pick one sub-category + one archetype.
 */
const UseCasePicker: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state || {}) as { archetypeId?: string; subCategory?: string; useCase?: string };

  // Deep-linked from a dashboard use-case card → skip straight to the audience step.
  const [screen, setScreen] = useState<1 | 2>(preset.useCase ? 2 : 1);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [freeUsed, setFreeUsed] = useState(false);
  const [useCase, setUseCase] = useState<string | null>(preset.useCase || null);
  const [subCategory, setSubCategory] = useState<string | null>(preset.subCategory || null);
  const [archetypeId, setArchetypeId] = useState<string | null>(preset.archetypeId || null);
  const [archetypes, setArchetypes] = useState<Archetype[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getUseCases(), getSubCategories(), getMarketplaceState()])
      .then(([u, s, st]) => { setUseCases(u); setSubs(s); setFreeUsed(st.free_study_used); })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMeta(false));
  }, []);

  useEffect(() => {
    if (!subCategory) { setArchetypes([]); return; }
    getArchetypes(subCategory).then(setArchetypes).catch((e) => setError(e.message));
  }, [subCategory]);

  // Returning users (free study used) go straight to the paid build flow.
  useEffect(() => {
    if (freeUsed) navigate('/marketplace/run/build', { replace: true, state: preset });
  }, [freeUsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedArch = archetypes.find((a) => a.archetype_id === archetypeId);
  const meta = useCase ? USE_CASE_META[useCase] : null;

  const onLaunch = () => {
    if (!useCase || !subCategory || !archetypeId) return;
    navigate('/marketplace/run/fill', {
      state: { useCase, selection: { sub_category: subCategory, archetype_id: archetypeId, name: selectedArch?.name, twin_count: selectedArch?.twin_count } },
    });
  };

  if (error) return <MarketplaceShell><div className="text-red-600">{error}</div></MarketplaceShell>;

  return (
    <MarketplaceShell>
      <StepIndicator step={screen} labels={['Choose use case', 'Select audience']} />

      {/* ── Screen 1: use case (flow entry — no back affordance) ────── */}
      {screen === 1 && (
        <>
          <div className="mb-7 flex items-start justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">What kind of study do you want to run?</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Pick one use case. You can run a different one next time.</p>
            </div>
            <FreeBadge />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {loadingMeta
              ? Array.from({ length: 4 }).map((_, i) => <UseCaseCardSkeleton key={i} />)
              : useCases.map((uc) => (
                  <UseCaseCard
                    key={uc.key}
                    useCaseKey={uc.key}
                    selected={useCase === uc.key}
                    onClick={() => setUseCase(uc.key)}
                  />
                ))}
          </div>

          <div className="mt-8 flex justify-end">
            <MktButton disabled={!useCase} onClick={() => setScreen(2)}>
              Next — select audience →
            </MktButton>
          </div>
        </>
      )}

      {/* ── Screen 2: audience ─────────────────────────────────────── */}
      {screen === 2 && meta && (
        <>
          <BackLink label="Choose use case" onClick={() => setScreen(1)} className="mb-3" />
          <div className="mb-5 flex items-start justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">Choose your audience segment</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Select a sub-category and one archetype for your free study.</p>
            </div>
            <FreeBadge />
          </div>

          {/* Selected use case summary (back is the single BackLink above). */}
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-black/10 bg-white px-4 py-3">
            <UseCaseTile useCaseKey={useCase!} />
            <div>
              <div className="text-[11px] text-[#6b6b6b]">Selected use case</div>
              <div className="text-[14px] font-medium text-[#111]">{meta.title}</div>
            </div>
          </div>

          <MktCard className="p-5">
            <div className="mb-2.5 text-[12px] text-[#6b6b6b]">Sub-category</div>
            <div className="flex flex-wrap gap-2">
              {subs.map((sc) => (
                <Pill
                  key={sc.slug}
                  active={subCategory === sc.slug}
                  onClick={() => { setSubCategory(sc.slug); setArchetypeId(null); }}
                >
                  {sc.name}
                </Pill>
              ))}
            </div>

            <div className="my-5 h-px bg-black/10" />

            <div className="mb-2.5 text-[12px] text-[#6b6b6b]">Archetype</div>
            <div className="flex flex-wrap gap-2">
              {archetypes.map((a) => {
                const sel = archetypeId === a.archetype_id;
                return (
                  <button
                    key={a.archetype_id}
                    onClick={() => setArchetypeId(a.archetype_id)}
                    className={[
                      'rounded-lg px-3.5 py-1.5 text-[13px] transition-colors border',
                      sel
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-[#6b6b6b] border-black/20 hover:border-black/40',
                    ].join(' ')}
                  >
                    {a.name}
                  </button>
                );
              })}
              {subCategory && archetypes.length === 0 && (
                <span className="text-[13px] text-[#999]">No archetypes found.</span>
              )}
            </div>

            <div className="my-4 h-px bg-black/10" />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-[#6b6b6b]">Selected:</span>
                {selectedArch && subCategory ? (
                  <>
                    <Tag>{subs.find((s) => s.slug === subCategory)?.name}</Tag>
                    <span className="inline-flex items-center rounded-full bg-[#f5f5f3] px-2 py-0.5 text-[11px] font-medium text-[#6b6b6b]">
                      {selectedArch.name}
                    </span>
                    <span className="text-[12px] text-[#6b6b6b]">
                      — <span className="font-medium text-[#111]">{selectedArch.twin_count}</span> Twins
                    </span>
                  </>
                ) : (
                  <span className="text-[13px] text-[#999]">None yet</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {!archetypeId && (
                  <span className="text-[12px] text-[#6b6b6b]">Select an archetype to continue</span>
                )}
                <MktButton disabled={!archetypeId} onClick={onLaunch}>
                  Launch study →
                </MktButton>
              </div>
            </div>
          </MktCard>
        </>
      )}
    </MarketplaceShell>
  );
};

export default UseCasePicker;
